import React, {useEffect} from 'react';
import {Box, Button, Grid, Typography} from '@mui/material';
import gql from 'graphql-tag';
import {useMutation} from '@apollo/client';
import {Replay} from '@mui/icons-material';
import {useSnackbar} from 'notistack';
import {Debug} from '../../contexts/DebugProvider';
import {GetLoginInfo_getCurrentUser} from '../../schema-types/GetLoginInfo';
import {
  GlobalPermissionEnum,
  PermGroupsEnum,
  UserPermissionEnum,
} from '../../schema-types/globalTypes';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {hasGroup, hasPermission} from '../../contexts/FeatureBoundary';
import {camelCaseToSpacedString} from '../../features/goals/Goals/utils';
import {getEnvironment} from '../../utils/envTest';
import {ChangeBusinessUnit} from '../Admin/ChangeBusinessUnit';
import {usePullAllModelPortfoliosAndProviders} from '../../hooks/usePullAllModelPortfoliosAndProviders';
import {LOGGED_IN_USER_FRAGMENT} from '../../hooks/useDjango';
import {usePullAllFeeComponents} from '../../hooks/usePullAllFeeComponents';

export const useGodMode = () => {
  const {djangoUser} = useDjangoContext();
  const [onSetUserGroup] = useMutation(
    gql`
      mutation SetUserGroup($staffId: UUID!, $group: PermGroupsEnum!) {
        changeUserGroup(staffId: $staffId, group: $group) {
          staff {
            ...LoggedInUser
          }
          ok
          errors
        }
      }
      ${LOGGED_IN_USER_FRAGMENT}
    `,
    {
      refetchQueries: ['GetBusinessUnits'],
    }
  );
  const onSetGroup = (group: PermGroupsEnum) =>
    onSetUserGroup({
      variables: {staffId: djangoUser.id, group},
    });

  return {onSetGroup};
};

export const SetGroupButton: React.FC<{group: PermGroupsEnum}> = ({group}) => {
  const {onSetGroup} = useGodMode();
  const {djangoUser} = useDjangoContext();
  return (
    <Box display="inline-flex" mr={1} mb={1}>
      <Button
        data-test={group}
        color={hasGroup(djangoUser, group) ? 'primary' : 'secondary'}
        size="small"
        onClick={async (e) => {
          await onSetGroup(group);
          if (e.altKey) {
            window.location.reload();
          }
        }}
        variant={hasGroup(djangoUser, group) ? 'contained' : 'outlined'}
      >
        Set as{' '}
        {camelCaseToSpacedString(group)
          .replace('Sfp', 'Saltus adviser')
          .replace('Na', 'Saltus non-adviser')
          .replaceAll('_', ' ')
          .toLocaleUpperCase()}
      </Button>
    </Box>
  );
};

export const DebugModal: React.FC<{
  setTesterModal: React.Dispatch<React.SetStateAction<boolean>>;
  djangoUser: GetLoginInfo_getCurrentUser;
  setDebug: React.Dispatch<React.SetStateAction<Debug>>;
  debug: Debug;
  perms: GlobalPermissionEnum[];
}> = ({debug, djangoUser, setDebug, setTesterModal, perms}) => {
  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.code === 'Escape') {
        setTesterModal(false);
      }
    }

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, []);
  const {pullAllModelPortfoliosAndProviders} = usePullAllModelPortfoliosAndProviders();
  const {pullAllFeeComponents} = usePullAllFeeComponents();
  const {enqueueSnackbar} = useSnackbar();

  return (
    <>
      <Box>
        {/* <Typography variant="body1">
            This is visible to only devs and testers. You can enable/disable permission
            mode (see whether a feature is SPP or Saltus only) and debug mode (view ids
            and form structures).
          </Typography> */}
        <Grid spacing={2} container>
          <Grid xs={4} item>
            {perms.includes(GlobalPermissionEnum.can_access_admin_features) ? (
              <Box>
                <Button
                  sx={{mb: 2}}
                  onClick={async () => {
                    window.open(getSentryReplaySearchUrl());
                  }}
                  color="primary"
                  variant="contained"
                >
                  <Replay htmlColor="white" /> View page replays
                </Button>
              </Box>
            ) : null}
            {[
              PermGroupsEnum.god,
              PermGroupsEnum.saltus_na,
              PermGroupsEnum.sfp,
              PermGroupsEnum.spp_ar,
              PermGroupsEnum.spp_da,
              PermGroupsEnum.pad_junior,
              PermGroupsEnum.pad_relationship_manager,
              PermGroupsEnum.pad_senior,
            ].map((group) => {
              return <SetGroupButton key={group} group={group} />;
            })}
            <Box mt={4} />
            <ChangeBusinessUnit onClose={() => setTesterModal(false)} type="buttons" />
          </Grid>
          <Grid xs={4} item>
            <Typography variant="h5">
              <strong>Debug mode:</strong>
            </Typography>
            <Box mt={1} />
            <Button
              color={debug?.forms ? 'secondary' : 'primary'}
              onClick={async (_e) => {
                setDebug((x) => ({...x, forms: true}));
              }}
              variant="contained"
            >
              Enable debug mode {debug?.forms ? ' ✅' : ''}
            </Button>
            <Box display="inline-block" pl={2} />
            <Button
              onClick={async (_e) => {
                setDebug((x) => ({...x, forms: false}));
              }}
              color={debug?.forms ? 'primary' : 'secondary'}
              variant="contained"
            >
              Disable debug mode {debug?.forms ? '' : ' ✅'}
            </Button>
            <Box mt={2} />
            <Typography variant="h5">
              <strong>My permissions:</strong>
            </Typography>
            <Grid container>
              {[
                ...Object.values(GlobalPermissionEnum),
                ...Object.values(UserPermissionEnum),
              ]?.map((x) => {
                return (
                  <Grid
                    item
                    xs={6}
                    key={x}
                    borderBottom="1px solid lightgrey"
                    p={0.5}
                    display="inline-flex"
                    flex={1}
                  >
                    {x
                      ?.replace('_FE', '')
                      .replace('can_access_', '')
                      .replaceAll('_', ' ')}{' '}
                    {hasPermission(djangoUser, [x]) ? '✅' : '❌'}
                  </Grid>
                );
              })}
            </Grid>
            <Box mt={2} />
            <Button
              onClick={async (_e) => {
                setDebug((x) => ({...x, permissions: !x.permissions}));
                setTesterModal(false);
              }}
              variant="contained"
            >
              {debug?.permissions ? 'Hide' : 'Show'} feature permission boundaries
            </Button>
            <Box mt={2} />
            <Button
              onClick={() => {
                enqueueSnackbar({
                  message: 'Pulling all model portfolios and providers',
                  variant: 'info',
                });
                pullAllModelPortfoliosAndProviders().then(({data}) => {
                  if (data?.pullAllModelPortfoliosAndProviders?.ok) {
                    enqueueSnackbar({
                      message: 'Pulled all model portfolios and providers',
                      variant: 'success',
                    });
                  } else {
                    enqueueSnackbar({
                      message: 'Could not pull model portfolios and providers',
                      variant: 'error',
                    });
                  }
                });
              }}
              variant="outlined"
            >
              Pull All Model Portfolios And Providers
            </Button>
            <Box mt={2} />
            <Button
              onClick={() => {
                enqueueSnackbar({
                  message: 'Pulling all fee components',
                  variant: 'info',
                });
                pullAllFeeComponents().then(({data}) => {
                  if (data?.pullAllFeeComponents?.ok) {
                    enqueueSnackbar({
                      message: 'Pulled all fee components',
                      variant: 'success',
                    });
                  } else {
                    enqueueSnackbar({
                      message: 'Could not pull fee components',
                      variant: 'error',
                    });
                  }
                });
              }}
              variant="outlined"
            >
              Pull All Fee Components
            </Button>
          </Grid>
        </Grid>
        <Box mt={2} />
      </Box>
    </>
  );
};

function getSentryReplaySearchUrl() {
  const {href} = window.location;
  const sentryAcceptable = href.replace(window.location.origin, '');
  const url = `https://saltus-partners-llp.sentry.io/replays/?environment=${getEnvironment()}&project=6765336&query=urls%3A${encodeURIComponent(
    sentryAcceptable
  )}&statsPeriod=10d`;
  return url;
}

export function getSentryReplayAccountUrl(accountName: string) {
  if (accountName) {
    const url = `https://saltus-partners-llp.sentry.io/explore/replays/?environment=${getEnvironment()}&statsPeriod=90d&query=accountName:${encodeURIComponent(
      `"${accountName}"`
    )}`;
    return url;
  }
}
